import { app, firestore } from "@/firebase";
import { ApiResponse } from "@/types/api";
import { handleFirestoreError } from "@/utils/firestoreHelpers";
import { FirestoreUser } from "@tunagari/shared/types/User";
import {
  collection,
  doc,
  DocumentData,
  endBefore,
  getDoc,
  getDocs,
  limit,
  limitToLast,
  orderBy,
  query,
  Query,
  startAfter,
  where,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

const functions = getFunctions(app);
const deleteUserFunction = httpsCallable(functions, "user-deleteUser");
const saveUserFunction = httpsCallable(functions, "user-saveUser");
const bulkSaveUserStatusFunction = httpsCallable(
  functions,
  "user-bulkSaveUserStatusByClientId"
);
const deleteUserByUidFunction = httpsCallable(
  functions,
  "user-deleteUserByUid"
);

export const adminDeleteUser = async (
  userId: string
): Promise<ApiResponse<null>> => {
  try {
    await deleteUserFunction({ userId });
    return { success: true };
  } catch (error) {
    return handleFirestoreError<null>(error, "deleting user");
  }
};

export const readUserByClientId = async (
  clientId: string
): Promise<ApiResponse<FirestoreUser | null>> => {
  try {
    const userQuery = query(
      collection(firestore, "users"),
      where("clientId", "==", clientId),
      limit(1)
    );
    const userSnapshot = await getDocs(userQuery);
    if (userSnapshot.empty) {
      return { success: true, data: null };
    }
    const userData = userSnapshot.docs[0].data() as FirestoreUser;
    return { success: true, data: userData };
  } catch (error) {
    return handleFirestoreError<FirestoreUser | null>(error, "reading user");
  }
};

export const fetchFirestoreUser = async (
  uid: string
): Promise<ApiResponse<FirestoreUser | null>> => {
  try {
    const userDocRef = doc(firestore, "users", uid);
    const userDoc = await getDoc(userDocRef);
    if (userDoc.exists()) {
      return { success: true, data: userDoc.data() as FirestoreUser };
    } else {
      return { success: false, error: "User not found" };
    }
  } catch (error) {
    return handleFirestoreError<FirestoreUser | null>(error, "fetching user");
  }
};

interface FetchUsersOptions {
  clientId?: string;
  email?: string;
  studyMemo?: string;
  firstVisibleId?: string | null;
  lastVisibleId?: string | null;
  pageSize?: number;
  direction?: "next" | "previous";
  orderByFieldPath?: "createdAt" | "updatedAt";
  orderByDirection?: "asc" | "desc";
}

export const fetchFirestoreUsers = async (
  options: FetchUsersOptions = {}
): Promise<
  ApiResponse<{
    userList: FirestoreUser[];
    firstVisible: DocumentData | null;
    lastVisible: DocumentData | null;
    hasNext: boolean;
    hasPrevious: boolean;
  }>
> => {
  const {
    clientId,
    email,
    studyMemo,
    firstVisibleId,
    lastVisibleId,
    pageSize = 10,
    direction = "next",
    orderByFieldPath = "createdAt",
    orderByDirection = "asc",
  } = options;

  const firstVisible = firstVisibleId
    ? await getDoc(doc(firestore, "users", firstVisibleId))
    : null;

  const lastVisible = lastVisibleId
    ? await getDoc(doc(firestore, "users", lastVisibleId))
    : null;

  let userQueryBase: Query<DocumentData> = collection(firestore, "users");

  // default to excluding admin users
  userQueryBase = query(userQueryBase, where("admin", "==", false));

  if (clientId) {
    userQueryBase = query(
      userQueryBase,
      where("clientId", ">=", clientId),
      where("clientId", "<=", clientId + "\uf8ff")
    );
  }

  if (email) {
    userQueryBase = query(
      userQueryBase,
      where("email", ">=", email),
      where("email", "<=", email + "\uf8ff")
    );
  }

  if (studyMemo) {
    userQueryBase = query(
      userQueryBase,
      where("studyMemo", ">=", studyMemo),
      where("studyMemo", "<=", studyMemo + "\uf8ff")
    );
  }

  let userQuery = query(userQueryBase);

  // When using inequality filters, we must order by that field first
  const orderByField = clientId
    ? "clientId"
    : email
    ? "email"
    : studyMemo
    ? "studyMemo"
    : orderByFieldPath;
  const orderByDir = clientId || email || studyMemo ? "asc" : orderByDirection;

  if (direction === "next") {
    if (lastVisible) {
      userQuery = query(
        userQuery,
        orderBy(orderByField, orderByDir),
        startAfter(lastVisible),
        limit(pageSize)
      );
    } else {
      userQuery = query(
        userQuery,
        orderBy(orderByField, orderByDir),
        limit(pageSize)
      );
    }
  } else if (direction === "previous") {
    if (firstVisible) {
      userQuery = query(
        userQuery,
        orderBy(orderByField, orderByDir),
        endBefore(firstVisible),
        limitToLast(pageSize)
      );
    } else {
      userQuery = query(
        userQuery,
        orderBy(orderByField, orderByDir),
        limitToLast(pageSize)
      );
    }
  }

  try {
    const userSnapshot = await getDocs(userQuery);
    const totalDocs = userSnapshot.size;

    // データをpageSize件に制限
    const userList = userSnapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        } as unknown as FirestoreUser)
    );

    // ドキュメントの表示位置
    const firstVisibleDoc = userSnapshot.docs[0] || null;
    const lastVisibleDoc = userSnapshot.docs[totalDocs - 1] || firstVisibleDoc;

    // ページングのための情報
    const hasNext = await getDocs(
      query(
        userQueryBase,
        orderBy(orderByField, orderByDir),
        startAfter(lastVisibleDoc),
        limit(1)
      )
    ).then((snapshot) => !snapshot.empty);
    const hasPrevious = await getDocs(
      query(
        userQueryBase,
        orderBy(orderByField, orderByDir),
        endBefore(firstVisibleDoc),
        limitToLast(1)
      )
    ).then((snapshot) => !snapshot.empty);

    return {
      success: true,
      data: {
        userList,
        firstVisible: firstVisibleDoc,
        lastVisible: lastVisibleDoc,
        hasNext,
        hasPrevious,
      },
    };
  } catch (error) {
    return handleFirestoreError<{
      userList: FirestoreUser[];
      firstVisible: DocumentData | null;
      lastVisible: DocumentData | null;
      hasNext: boolean;
      hasPrevious: boolean;
    }>(error, "fetching users");
  }
};

export const saveFirestoreUser = async (
  uid: string,
  user: Partial<FirestoreUser>,
  options: {
    clientId?: string; // クライアントID（メッセージ送信に必要）
    statusDate?: string;
  } = {}
): Promise<ApiResponse<void>> => {
  try {
    const result = await saveUserFunction({ uid, user, options });
    return { success: true, data: undefined };
  } catch (error) {
    return handleFirestoreError<void>(error, "saving user");
  }
};

export const deleteFirestoreUserByUid = async (
  uid: string
): Promise<ApiResponse<void>> => {
  try {
    await deleteUserByUidFunction({ uid });
    return { success: true };
  } catch (error) {
    return handleFirestoreError<void>(error, "deleting user");
  }
};

export const bulkSaveUserStatusByClientId = async (
  users: ({ clientId: string } & Partial<Omit<FirestoreUser, "clientId">>)[]
): Promise<ApiResponse<{ missingClientIds?: string[] }>> => {
  try {
    const result = await bulkSaveUserStatusFunction({ users });
    return {
      success: true,
      data: result.data as { missingClientIds?: string[] },
    };
  } catch (error) {
    return handleFirestoreError<{ missingClientIds?: string[] }>(
      error,
      "bulk saving user status"
    );
  }
};

export const fetchFirestoreUsersAll = async (): Promise<
  ApiResponse<{
    userList: FirestoreUser[];
  }>
> => {
  let userQueryBase: Query<DocumentData> = collection(firestore, "users");
  userQueryBase = query(userQueryBase, where("admin", "==", false));

  let userQuery = query(userQueryBase, orderBy("createdAt", "asc"));

  try {
    const userSnapshot = await getDocs(userQuery);

    // データをpageSize件に制限
    const userList = userSnapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        } as unknown as FirestoreUser)
    );

    return {
      success: true,
      data: {
        userList,
      },
    };
  } catch (error) {
    return handleFirestoreError<{
      userList: FirestoreUser[];
    }>(error, "fetching all users");
  }
};
